/** @format */

import React from 'react';
import {
  htmlToCharacterCheck,
  unescapeHtml,
} from '@services/string-transformation-services';
import Countdown from 'react-countdown';
import { getDrupalDateTime } from '@services/date-services';
import CountdownBanner from '@paragraphs/banners/countdown-banner';

type CountdownRendererProps = {
  days?: number;
  hours?: number;
  minutes?: number;
  seconds?: number;
  completed?: boolean;
};

type CountdownBannerProps = {
  additionalText?: string;
  subtitle?: string;
  title?: string;
  disclaimer?: string;
  linkText?: string;
  linkUrl?: string;
  bannerStyle: string;
  autoSwitchBanner: boolean;
  countdownMobileBanner?: string;
  countdownDesktopBanner?: string;
  countdownBackgroundColor?: string;
  countdownEndTime?: string;
  postCountdownMobileBanner?: string;
  postCountdownDesktopBanner?: string;
  postCountdownTitle?: string;
  postCountdownSubtitle?: string;
  postCountdownAddText?: string;
  postCountdownLinkUrl?: string;
  postCountdownLinkText?: string;
};

const CountdownBannerWrapper: React.FC<{
  paragraphData: CountdownBannerProps;
}> = ({ paragraphData }) => {
  const {
    additionalText,
    subtitle,
    title,
    disclaimer,
    linkText,
    linkUrl,
    autoSwitchBanner,
    countdownMobileBanner,
    countdownDesktopBanner,
    countdownEndTime,
    postCountdownMobileBanner,
    postCountdownDesktopBanner,
    postCountdownTitle,
    postCountdownSubtitle,
    postCountdownAddText,
    postCountdownLinkUrl,
    postCountdownLinkText,
  } = paragraphData;

  const safeAdditionalText = unescapeHtml(additionalText);
  const safeTitle = htmlToCharacterCheck(title);
  const safeSubtitle = htmlToCharacterCheck(subtitle);
  const safeLinkText = htmlToCharacterCheck(linkText);
  const safePostTitle = htmlToCharacterCheck(postCountdownTitle);
  const safePostSubtitle = htmlToCharacterCheck(postCountdownSubtitle);
  const safePostAdditionalText = unescapeHtml(postCountdownAddText);
  const safePostLinkText = htmlToCharacterCheck(postCountdownLinkText);
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const safeAutoswitch = autoSwitchBanner === '1';
  const safeDateTime = getDrupalDateTime(countdownEndTime || '');
  const safePostMobileBanner =
    postCountdownMobileBanner || postCountdownDesktopBanner;
  const safeMobileBanner = countdownMobileBanner || countdownDesktopBanner;

  /**
   * Custom countdown renderer.
   */
  const renderer = ({
    days,
    hours,
    minutes,
    seconds,
    completed,
  }: CountdownRendererProps) => {
    if (completed && safeAutoswitch) {
      /**
       * Post countdown banner. Show this banner if auto switch is enabled and the
       * timer has reached zero.
       */
      return (
        <CountdownBanner
          title={safePostTitle}
          subtitle={safePostSubtitle}
          additionalText={safePostAdditionalText}
          disclaimer={disclaimer}
          linkText={safePostLinkText}
          linkUrl={postCountdownLinkUrl}
          postBanner={true}
          mobileBanner={safePostMobileBanner}
          desktopBanner={postCountdownDesktopBanner}
        />
      );
    }

    return (
      <CountdownBanner
        title={safeTitle}
        subtitle={safeSubtitle}
        additionalText={safeAdditionalText}
        disclaimer={disclaimer}
        linkText={safeLinkText}
        linkUrl={linkUrl}
        postBanner={false}
        mobileBanner={safeMobileBanner}
        desktopBanner={countdownDesktopBanner}
        dateTime={safeDateTime}
        days={days}
        hours={hours}
        minutes={minutes}
        seconds={seconds}
        completed={completed}
      />
    );
  };

  /**
   * Render.
   */
  return (
    <div className="container">
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore */}
      <Countdown date={safeDateTime} renderer={renderer} />
    </div>
  );
};

export default CountdownBannerWrapper;
